import {
    ArrowLeft, Check, DoorOpen, Pencil, Trash2, Trophy, UserPlus, Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { Podium } from '../../components/Podium.tsx';
import { useHaptics } from '../../hooks/useHaptics.ts';
import { ApiError } from '../../lib/api.ts';
import type { Club } from '../../lib/equipment.ts';
import { listClubs } from '../../lib/equipment.ts';
import type { Friend } from '../../lib/friends.ts';
import { listFriends } from '../../lib/friends.ts';
import { useAuth } from '../../lib/auth.tsx';
import type { PuttingState } from '../../lib/putting.ts';
import { allure } from '../rounds/Carte.tsx';
import {
    clearHoleScore, forgetGame, formatAvg, getGame, inviteMore, leaveGame,
    openGameStream, setHoleScore, setMyClub,
} from '../../lib/putting.ts';

/* La partie en direct.
 *
 * ── Comment les scores des autres arrivent ──────────────────────────────────
 *
 * Par un flux poussé depuis le serveur (SSE). Un trigger prévient Postgres dès
 * qu'une partie bouge, l'API recalcule l'état pour chaque spectateur et le
 * pousse : de l'appui d'un joueur à l'affichage chez les autres, il s'écoule
 * une fraction de seconde.
 *
 * Ce choix tient à un réglage de l'application : l'écran reste allumé pendant
 * une séance. Un flux poussé n'aurait pas survécu à des téléphones qui se
 * verrouillent entre deux putts — les navigateurs mobiles suspendent tout en
 * arrière-plan — mais ici la page reste au premier plan, et la connexion avec
 * elle.
 *
 * ── La relève de secours reste, et ce n'est pas une ceinture de plus ────────
 *
 * Trois choses lui échappent, et le flux ne peut rien pour aucune : les
 * notifications émises pendant une coupure de la connexion d'écoute sont
 * perdues ; un jeton d'accès qui expire fait abandonner EventSource, qui ne
 * retente pas sur une réponse non conforme ; et un réseau de practice coupe
 * sans prévenir. La relève tourne donc en fond, lentement quand le flux est
 * ouvert, vite quand il ne l'est plus — et ses appels rafraîchissent la
 * session au passage, ce qui permet au flux de repartir.
 *
 * ── Ce que la saisie ne fait pas ────────────────────────────────────────────
 *
 * Elle n'attend pas son tour. Chacun tape quand c'est à lui et enregistre ce
 * qu'il vient de faire ; deux joueurs peuvent être sur des trous différents.
 * D'où un classement à la moyenne par trou joué, et non au total brut — qui
 * mettrait mécaniquement en tête celui qui a le moins avancé.
 */

/** Flux ouvert : simple filet de sécurité contre un évènement manqué. */
const POLL_SLOW_MS = 20_000;
/** Flux rompu : c'est la relève qui fait tout le travail. */
const POLL_FAST_MS = 3_000;

/** Le pad de saisie. Six valeurs suffisent : au-delà, on ne compte plus, on
 *  ramasse sa balle. La correction, elle, accepte tout ce que la base accepte. */
const PUTT_CHOICES = [1, 2, 3, 4, 5, 6];

/** Le contrat d'un green : deux putts.
 *
 *  C'est le par du putting, et c'est déjà la coupure que retiennent les
 *  statistiques — un putt est gagné, deux est le contrat, trois est perdu. La
 *  carte s'écrit donc dans le même vocabulaire de formes que celle d'un tour de
 *  parcours : cercle en dessous, carré au-dessus. */
const REGULATION = 2;

/** La carte d'un joueur : une case par trou.
 *
 *  Les formes viennent du ruban du parcours, et c'est tout l'intérêt — un
 *  cercle veut dire la même chose sur les deux écrans, sans qu'on ait à le
 *  réapprendre. La couleur ne dit jamais rien que la forme ne dise aussi :
 *  vert et rouge sont indiscernables pour huit pour cent des hommes.
 *
 *  Interactive pendant la partie — c'est le chemin de la correction — et
 *  simple relevé une fois qu'elle est finie. */
function GrilleTrous({ scores, nbTrous, actif, onChoisir }: {
    scores: Record<string, number>;
    nbTrous: number;
    /** Le trou en cours de saisie, souligné. Absent sur une carte terminée :
     *  aucun trou n'y est « en cours ». */
    actif?: number | null;
    onChoisir?: (n: number) => void;
}) {
    /* 9 et 18 se rangent par neuf, 6 et 12 par six. Chaque format tombe alors
       sur un rectangle plein : une dernière ligne à trois cases se lit comme
       un trou manquant. Et tout tient sans défiler — une carte de fin de
       partie se montre aux autres, elle ne se fait pas glisser. */
    const colonnes = nbTrous % 9 === 0 ? 'grid-cols-9' : 'grid-cols-6';
    return (
        <div className={`grid ${colonnes} gap-1`}>
            {Array.from({ length: nbTrous }, (_, i) => i + 1).map((h) => {
                const v = scores[String(h)];
                const ici = h === actif;
                const mot = `Trou ${h}${v === undefined
                    ? ' : pas encore joué' : ` : ${v} coup${v > 1 ? 's' : ''}`}`;
                const dedans = (
                    <>
                        <span className={`text-[10px] leading-none tabular-nums ${
                            ici ? 'font-bold text-primary' : 'opacity-50'}`}>
                            {h}
                        </span>
                        <span className={`flex size-7 items-center justify-center text-xs tabular-nums ${
                            allure(v === undefined ? null : v - REGULATION)
                        } ${ici ? 'ring-2 ring-primary ring-offset-1 ring-offset-base-100' : ''}`}>
                            {v ?? ''}
                        </span>
                    </>
                );
                const habit = 'flex flex-col items-center gap-0.5 py-0.5';
                return onChoisir
                    ? (
                        <button key={h} type="button" onClick={() => onChoisir(h)}
                            aria-label={mot} aria-current={ici ? 'true' : undefined}
                            className={habit}>
                            {dedans}
                        </button>
                    )
                    : <span key={h} className={habit} aria-label={mot}>{dedans}</span>;
            })}
        </div>
    );
}

export function PuttingGame() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { vibrate } = useHaptics();

    const [state, setState] = useState<PuttingState | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [activeHole, setActiveHole] = useState<number | null>(null);
    const [showClubs, setShowClubs] = useState(false);
    const [showInvite, setShowInvite] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [putters, setPutters] = useState<Club[]>([]);
    const [friends, setFriends] = useState<Friend[]>([]);
    const [streamOpen, setStreamOpen] = useState(false);

    // La relève ne doit pas écraser un score qu'on vient d'enregistrer : toute
    // écriture rend l'état à jour, et une réponse de relève partie AVANT elle
    // pourrait revenir après. Ce compteur fait ignorer les réponses périmées.
    const writeSeq = useRef(0);
    // Même problème sur le flux, mais impossible à horodater côté client : c'est
    // le serveur qui déclenche l'envoi. On ignore donc les états poussés pendant
    // qu'une écriture est en vol. Un état parti juste avant notre COMMIT et
    // arrivé juste après resterait théoriquement possible ; il est corrigé dans
    // la foulée par l'envoi que notre propre écriture provoque.
    const writesInFlight = useRef(0);

    // Calculé avant les retours anticipés : les hooks qui en dépendent doivent
    // s'exécuter à chaque rendu, y compris pendant le chargement.
    const live = state?.game.status === 'open';

    const load = useCallback(async (silent: boolean) => {
        if (!id) return;
        const seq = writeSeq.current;
        try {
            const s = await getGame(id);
            if (seq === writeSeq.current) setState(s);
        } catch (e) {
            if (!silent) setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        }
    }, [id]);

    useEffect(() => { void load(false); }, [load]);

    // Flux temps réel. Ouvert tant que la partie l'est ; refermé proprement en
    // quittant l'écran, sinon chaque aller-retour dans l'application laisserait
    // une connexion ouverte derrière lui.
    useEffect(() => {
        if (!id || !live) return undefined;
        return openGameStream(
            id,
            (s) => { if (writesInFlight.current === 0) setState(s); },
            setStreamOpen,
        );
    }, [id, live]);

    // Relève de secours. Lente quand le flux tient, rapide quand il est rompu —
    // et immédiate au retour sur l'écran, qui est l'instant où l'on regarde.
    useEffect(() => {
        if (!live) return undefined;
        const period = streamOpen ? POLL_SLOW_MS : POLL_FAST_MS;
        let timer: number | undefined;
        const tick = (): void => {
            if (document.visibilityState === 'visible') void load(true);
            timer = window.setTimeout(tick, period);
        };
        timer = window.setTimeout(tick, period);

        const onVisible = (): void => {
            if (document.visibilityState === 'visible') void load(true);
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);

        return () => {
            if (timer) window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [live, streamOpen, load]);

    const write = async (fn: () => Promise<PuttingState>): Promise<void> => {
        setBusy(true); setError(null);
        writeSeq.current += 1;
        writesInFlight.current += 1;
        try {
            setState(await fn());
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
            void load(true);
        } finally {
            writesInFlight.current -= 1;
            setBusy(false);
        }
    };

    if (error && !state) {
        return <div role="alert" className="alert alert-error mt-4"><span>{error}</span></div>;
    }
    if (!state || !id) {
        return (
            <div className="flex justify-center py-16">
                <span className="loading loading-ring loading-lg text-primary" />
            </div>
        );
    }

    const { game, players, me } = state;
    const playing = me?.status === 'accepted' && !me.leftAt && live;
    const isHost = game.hostId === user?.id;
    const standings = players.filter((p) => p.status === 'accepted');
    const pending = players.filter((p) => p.status === 'pending');
    // Seul inscrit : me retirer fait disparaître la partie entière, et le
    // bouton doit le dire — « retirer de mon historique » laisserait croire
    // qu'elle survit quelque part.
    const soleSurvivor = players.length === 1;

    /* Les trois marches, une fois la partie finie. Seuls ceux qui ont un score
       y montent : un joueur invité qui n'a pas rentré un putt n'est pas
       troisième, il n'a pas joué. `standings` est déjà rendu classé par le
       serveur, à la moyenne par trou. */
    const podium = standings.filter((p) => p.holesPlayed > 0).slice(0, 3);
    /* Tout le monde n'a pas forcément joué autant de trous — on quitte un
       green plus tôt que les autres. Le total ne se compare alors plus, et il
       faut dire au lecteur ce qui classe vraiment. */
    const inegal = new Set(standings.filter((p) => p.holesPlayed > 0)
        .map((p) => p.holesPlayed)).size > 1;

    // Le trou courant : le premier non joué. Explicite dès qu'on touche la
    // carte, pour corriger un score sans perdre sa place.
    const nextHole = Array.from({ length: game.holeCount }, (_, i) => i + 1)
        .find((h) => me?.holes[String(h)] === undefined) ?? null;
    const hole = activeHole ?? nextHole;

    const forget = async (): Promise<void> => {
        setBusy(true); setError(null);
        try {
            await forgetGame(id);
            void navigate('/putting');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
            setBusy(false);
            setConfirmDelete(false);
        }
    };

    const record = async (putts: number): Promise<void> => {
        if (!hole) return;
        vibrate('success');
        await write(() => setHoleScore(id, hole, putts));
        // On repart sur le trou suivant de soi-même : rester sur le trou qu'on
        // vient de rentrer obligerait à un geste de plus à chaque coup.
        setActiveHole(null);
    };

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/putting" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Putting
                </Link>
                <div className="flex items-start justify-between gap-3">
                    {/* Une fois la partie finie, c'est ça qu'on vient lire.
                        Le nom passe en surtitre et « Partie terminée » prend la
                        grande ligne — le même renversement que sur la carte d'un
                        tour de parcours, et pour la même raison : on sait d'où
                        l'on vient, on veut savoir que c'est fini. */}
                    <div className="min-w-0">
                        {live ? (
                            <>
                                <h1 className="font-display truncate text-2xl font-bold tracking-tight">
                                    {game.name ?? `Partie de ${game.hostName ?? 'putting'}`}
                                </h1>
                                <p className="text-sm opacity-60">
                                    {game.holeCount} trous · {standings.length} joueur
                                    {standings.length > 1 ? 's' : ''}
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="truncate text-[11px] font-bold uppercase tracking-[0.14em]"
                                    style={{ color: 'var(--tuile-encre)' }}>
                                    {game.name ?? `Partie de ${game.hostName ?? 'putting'}`}
                                    <span className="opacity-60">{' · '}{game.holeCount} trous</span>
                                </p>
                                <h1 className="font-display text-2xl font-bold tracking-tight">
                                    Partie terminée
                                </h1>
                            </>
                        )}
                    </div>
                    {/* L'état réel du flux, et non un décor.
                        Sur le réseau d'un practice la connexion tombe ; dire
                        « en direct » pendant ce temps ferait croire à un
                        classement figé que personne ne met à jour. */}
                    {live && (
                        streamOpen ? (
                            <span className="badge badge-primary badge-sm shrink-0 gap-1">
                                <span className="inline-block size-1.5 animate-pulse rounded-full bg-current" />
                                en direct
                            </span>
                        ) : (
                            <span className="badge badge-ghost badge-sm shrink-0 gap-1">
                                <span className="inline-block size-1.5 rounded-full bg-current opacity-50" />
                                reconnexion
                            </span>
                        )
                    )}
                </div>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {/* ── Saisie ──────────────────────────────────────────────────── */}
            {playing && hole !== null && (
                <section className="rounded-box bg-base-200 p-4">
                    <div className="flex items-baseline justify-between">
                        <h2 className="text-lg font-bold">Trou {hole}</h2>
                        {activeHole !== null && (
                            <button type="button" className="btn btn-ghost btn-xs"
                                onClick={() => setActiveHole(null)}>
                                Revenir au trou {nextHole ?? game.holeCount}
                            </button>
                        )}
                    </div>
                    <p className="mt-0.5 text-xs opacity-60">
                        Combien de coups pour rentrer ?
                    </p>

                    <div className="mt-3 grid grid-cols-3 gap-2">
                        {PUTT_CHOICES.map((n) => {
                            const current = me?.holes[String(hole)];
                            return (
                                <button key={n} type="button" disabled={busy}
                                    onClick={() => void record(n)}
                                    aria-pressed={current === n}
                                    className={`h-16 rounded-field text-2xl font-bold tabular-nums transition-colors ${
                                        current === n
                                            ? 'bg-primary text-primary-content'
                                            : 'bg-base-100 hover:bg-base-300'
                                    }`}>
                                    {n}
                                </button>
                            );
                        })}
                    </div>

                    {me?.holes[String(hole)] !== undefined && (
                        <button type="button" disabled={busy}
                            onClick={() => void write(() => clearHoleScore(id, hole))}
                            className="btn btn-ghost btn-sm btn-block mt-2">
                            Effacer ce trou
                        </button>
                    )}
                </section>
            )}

            {playing && hole === null && (
                <section className="rounded-box border border-base-300 bg-base-100 p-4 text-center shadow-sm">
                    <Check size={32} className="mx-auto text-primary" />
                    <p className="mt-2 font-semibold">Votre carte est complète.</p>
                    {/* La partie ne dépend plus d'un bouton « quitter » que
                        personne ne touche sur un green : elle se ferme quand
                        chacun a rentré son dernier putt. Le dire évite d'attendre
                        un geste qui n'a plus lieu d'être. */}
                    <p className="mt-1 text-xs opacity-60">
                        Elle se terminera d'elle-même quand chacun aura rentré son
                        dernier putt.
                    </p>
                </section>
            )}

            {/* ── Ma carte ────────────────────────────────────────────────── */}
            {/* Une fois la partie finie, ma carte cede la place aux cartes de
                TOUT le monde, plus bas : a ce moment-la on ne verifie plus la
                sienne, on compare. */}
            {me?.status === 'accepted' && live && (
                <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                    {/* Même habillage que le classement : deux cartes blanches
                        à titre en capitales, parce que ce sont les deux objets
                        de l'écran. */}
                    <div className="flex items-baseline justify-between px-0.5">
                        <h2 className="text-[11px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: 'var(--tuile-encre)' }}>
                            Votre carte
                        </h2>
                        <span className="font-score text-base tabular-nums opacity-70">
                            {me.totalPutts} coups · {formatAvg(me.avgPutts)}/trou
                        </span>
                    </div>
                    {/* Une case par trou, touchable : c'est le chemin de la
                        correction, et il n'a pas besoin d'être expliqué. */}
                    <div className="mt-3">
                        <GrilleTrous scores={me.holes} nbTrous={game.holeCount}
                            actif={hole}
                            onChoisir={playing ? (h) => setActiveHole(h) : undefined} />
                    </div>
                </section>
            )}

            {/* ── Classement ──────────────────────────────────────────────── */}
            {live && (
            <section className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                <h2 className="flex items-center gap-2 px-0.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                    style={{ color: 'var(--tuile-encre)' }}>
                    <Trophy size={14} style={{ color: 'var(--brass-ink)' }} />
                    {live ? 'Classement en direct' : 'Résultat'}
                </h2>
                <p className="mb-2 mt-1 px-0.5 text-xs opacity-60">
                    Au nombre de coups par trou joué : tout le monde n'est pas
                    forcément au même trou.
                </p>

                <div className="space-y-2">
                    {standings.map((p) => {
                        /* Le vainqueur porte le laiton — la couleur de ce qui est
                           acquis — mais seulement une fois la partie finie : en
                           direct, un premier de mi-parcours n'a rien gagné. */
                        const gagnant = !live && p.position === 1 && p.holesPlayed > 0;
                        const moiMeme = p.userId === user?.id;
                        return (
                            <div key={p.userId}
                                style={gagnant ? undefined : {
                                    backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)',
                                }}
                                className={`flex items-center gap-3 rounded-field p-2 ${
                                    gagnant ? 'bg-accent text-accent-content shadow-md' : ''
                                } ${moiMeme ? 'ring-2 ring-primary' : ''}`}>
                                <span className="font-score w-5 shrink-0 text-center text-lg leading-none tabular-nums">
                                    {p.holesPlayed > 0 ? p.position : '—'}
                                </span>
                                <Avatar name={p.displayName} avatarUrl={p.avatarUrl} size={32} />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-bold">
                                        {moiMeme ? 'vous' : p.displayName}
                                        {p.leftAt && live && (
                                            <span className="ml-1 text-xs font-normal opacity-60">sorti</span>
                                        )}
                                    </span>
                                    <span className="block text-xs opacity-70">
                                        {p.holesPlayed}/{game.holeCount} trous · {p.totalPutts} coups
                                        {p.clubLabel && ` · ${p.clubLabel}`}
                                    </span>
                                </span>
                                <span className="shrink-0 text-right">
                                    <span className="font-score block text-xl leading-none tabular-nums">
                                        {formatAvg(p.avgPutts)}
                                    </span>
                                    <span className="block text-[10px] uppercase tracking-wide opacity-60">
                                        coups/trou
                                    </span>
                                </span>
                            </div>
                        );
                    })}
                </div>

                {pending.length > 0 && (
                    <p className="mt-3 text-xs opacity-60">
                        En attente de réponse : {pending.map((p) => p.displayName).join(', ')}
                    </p>
                )}
            </section>
            )}

            {/* ── Le résultat : podium, puis les cartes ────────────────── */}
            {/* La même page qu'à la fin d'un tour de parcours, et à dessein.
                Une partie de putting se finit comme un parcours se finit — on
                se compare, puis on relit son propre parcours trou par trou — et
                l'écran n'avait ici qu'une liste classée, sans estrade et sans
                les cartes des autres. Deux façons de terminer une partie ne
                doivent pas donner deux façons de la lire.

                On y arrive par deux chemins : le dernier putt rentré, qui ferme
                la partie, et l'historique du putting. C'est le même écran. */}
            {!live && (
                <>
                    <Podium moiId={user?.id}
                        joueurs={podium.map((p) => ({
                            userId: p.userId,
                            displayName: p.displayName,
                            avatarUrl: p.avatarUrl,
                            valeur: String(p.totalPutts),
                            /* La moyenne SOUS le total, et seulement quand tout
                               le monde n'a pas joué le même nombre de trous :
                               c'est elle qui classe, et sans elle un podium
                               dont le premier affiche le plus gros total
                               passerait pour une erreur. */
                            detail: inegal ? `${formatAvg(p.avgPutts)}/trou` : null,
                        }))} />

                    {inegal && podium.length > 1 && (
                        <p className="-mt-2 text-center text-xs opacity-60">
                            Classé au nombre de coups par trou joué : tout le monde
                            n'a pas joué le même nombre de trous.
                        </p>
                    )}

                    {standings.map((p, rang) => {
                        const moiMeme = p.userId === user?.id;
                        return (
                            <section key={p.userId}
                                className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                                <div className="flex items-center gap-3 px-1">
                                    {/* Le rang ne s'écrit ici que faute de
                                        podium : quand il y en a un, le
                                        classement est déjà dessus, en grand et
                                        sur trois marches. */}
                                    {standings.length > 1 && podium.length < 2 && (
                                        <span className="w-5 shrink-0 text-center text-sm font-bold tabular-nums opacity-70">
                                            {p.holesPlayed > 0 ? p.position ?? rang + 1 : '–'}
                                        </span>
                                    )}
                                    <Avatar name={p.displayName} avatarUrl={p.avatarUrl} size={36} />
                                    <span className="min-w-0 flex-1 truncate">
                                        <span className={`block truncate ${
                                            moiMeme ? 'font-bold' : 'font-semibold opacity-80'}`}>
                                            {moiMeme ? 'vous' : p.displayName}
                                        </span>
                                        <span className="block truncate text-xs opacity-60">
                                            {p.holesPlayed}/{game.holeCount} trous
                                            {p.clubLabel ? ` · ${p.clubLabel}` : ''}
                                        </span>
                                    </span>
                                    <span className="shrink-0 text-right">
                                        <span className="block text-lg font-bold leading-none tabular-nums">
                                            {p.holesPlayed > 0 ? p.totalPutts : '—'}
                                        </span>
                                        <span className="block text-xs font-semibold tabular-nums opacity-60">
                                            {formatAvg(p.avgPutts)}/trou
                                        </span>
                                    </span>
                                </div>

                                <div className="mt-2">
                                    <GrilleTrous scores={p.holes} nbTrous={game.holeCount} />
                                </div>
                            </section>
                        );
                    })}

                    {standings.length === 0 && (
                        <p className="rounded-box border border-dashed border-base-300 p-8 text-center text-sm opacity-60">
                            Aucun score sur cette partie.
                        </p>
                    )}
                </>
            )}

            {/* ── Actions ─────────────────────────────────────────────────── */}
            {playing && (
                <div className="space-y-2">
                    <button type="button" className="btn btn-ghost btn-block justify-start gap-3"
                        onClick={() => { setShowClubs(true); if (putters.length === 0) void listClubs().then((c) => setPutters(c.filter((x) => x.category === 'putter'))); }}>
                        <Pencil size={18} />
                        {me?.clubLabel ? `Putter : ${me.clubLabel}` : 'Choisir mon putter'}
                    </button>

                    {isHost && (
                        <button type="button" className="btn btn-ghost btn-block justify-start gap-3"
                            onClick={() => { setShowInvite(true); if (friends.length === 0) void listFriends().then(setFriends); }}>
                            <UserPlus size={18} /> Inviter d'autres partenaires
                        </button>
                    )}

                    <button type="button" className="btn btn-outline btn-block gap-2"
                        onClick={() => setConfirmLeave(true)}>
                        <DoorOpen size={18} /> Quitter la partie
                    </button>
                </div>
            )}

            {me?.leftAt && live && (
                <div className="rounded-box border border-dashed border-base-300 p-4 text-center text-sm opacity-70">
                    Vous avez quitté cette partie. Elle continue sans vous.
                </div>
            )}

            {/* ── Feuilles ────────────────────────────────────────────────── */}
            {showClubs && (
                <Sheet title="Votre putter" onClose={() => setShowClubs(false)}>
                    {putters.length === 0 ? (
                        <p className="text-sm opacity-70">
                            Aucun putter dans votre matériel. La partie compte quand même,
                            mais elle n'alimentera pas de statistiques par club.
                        </p>
                    ) : putters.map((p) => (
                        <button key={p.id} type="button" disabled={busy}
                            onClick={() => { void write(() => setMyClub(id, p.id)); setShowClubs(false); }}
                            aria-pressed={me?.clubId === p.id}
                            className={`flex w-full items-center rounded-field p-3 text-left transition-colors ${
                                me?.clubId === p.id ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                            }`}>
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold">{p.label}</span>
                                {(p.brand ?? p.model) && (
                                    <span className="block truncate text-xs opacity-70">
                                        {[p.brand, p.model].filter(Boolean).join(' ')}
                                    </span>
                                )}
                            </span>
                        </button>
                    ))}
                </Sheet>
            )}

            {showInvite && (
                <Sheet title="Inviter" onClose={() => setShowInvite(false)}>
                    {friends.filter((f) => !players.some((p) => p.userId === f.id)).length === 0 ? (
                        <p className="text-sm opacity-70">
                            Tous vos partenaires sont déjà dans la partie.
                            <Link to="/amis" className="btn btn-ghost btn-sm mt-2 gap-2">
                                <Users size={16} /> Ajouter un partenaire
                            </Link>
                        </p>
                    ) : friends
                        .filter((f) => !players.some((p) => p.userId === f.id))
                        .map((f) => (
                            <button key={f.id} type="button" disabled={busy}
                                onClick={() => { void write(() => inviteMore(id, [f.id])); setShowInvite(false); }}
                                className="flex w-full items-center gap-3 rounded-field bg-base-100 p-2 text-left hover:bg-base-300">
                                <Avatar name={f.displayName} email={f.email}
                                    avatarUrl={f.avatarUrl} size={36} />
                                <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                                    {f.displayName}
                                </span>
                            </button>
                        ))}
                </Sheet>
            )}

            {confirmLeave && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">Quitter la partie ?</h2>
                        <p className="mt-2 text-sm opacity-80">
                            Vos scores sont conservés et restent au classement.
                        </p>
                        <p className="mt-2 text-xs opacity-60">
                            {standings.filter((p) => !p.leftAt).length <= 1
                                ? 'Vous êtes le dernier en jeu : la partie se terminera et les résultats seront figés.'
                                : 'La partie continue pour les autres. Vous pourrez revenir tant qu\'elle est en cours.'}
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy}
                                onClick={() => {
                                    void write(() => leaveGame(id));
                                    setConfirmLeave(false);
                                }}
                                className="btn btn-error btn-block tap-target">
                                Quitter
                            </button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmLeave(false)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}

            {!live && (
                <>
                    <button type="button" onClick={() => void navigate('/putting')}
                        className="btn btn-primary btn-block">
                        Retour au putting
                    </button>

                    {/* En bas, après tout ce qu'elle retire : on la trouve en
                        la cherchant, pas en effleurant l'écran. */}
                    {me && (
                        <div className="pt-4">
                            <button type="button" className="btn btn-ghost btn-sm gap-2 text-error"
                                onClick={() => setConfirmDelete(true)}>
                                <Trash2 size={16} />
                                {soleSurvivor ? 'Supprimer cette partie' : 'Retirer de mon historique'}
                            </button>
                        </div>
                    )}
                </>
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">
                            {soleSurvivor ? 'Supprimer cette partie ?' : 'Retirer cette partie ?'}
                        </h2>
                        <p className="mt-2 text-sm opacity-80">
                            {new Date(game.startedAt).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long',
                            })}
                            {' — '}{me?.holesPlayed ?? 0} trou{(me?.holesPlayed ?? 0) > 1 ? 's' : ''},{' '}
                            {me?.totalPutts ?? 0} coups.
                        </p>
                        {/* Dire précisément ce qui part et ce qui reste : le
                            réflexe, sur une partie partagée, est de croire
                            qu'on l'efface pour tout le monde. */}
                        <p className="mt-2 text-xs opacity-60">
                            {soleSurvivor
                                ? 'Vos scores et cette partie disparaissent définitivement. Vos statistiques de putter seront recalculées sans eux.'
                                : `Vos scores disparaissent et vos statistiques de putter seront recalculées sans eux. Les cartes des autres joueurs restent intactes, et vous ne figurerez plus à leur classement.`}
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy}
                                onClick={() => void forget()}
                                className="btn btn-error btn-block gap-2 tap-target">
                                <Trash2 size={18} />
                                {soleSurvivor ? 'Supprimer définitivement' : 'Retirer'}
                            </button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmDelete(false)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/** Feuille montante, ancrée en bas : sur un téléphone tenu à une main, le bas
 *  de l'écran est la seule zone qu'un pouce atteint sans se replacer. */
function Sheet({ title, onClose, children }: {
    title: string; onClose: () => void; children: React.ReactNode;
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center"
            onClick={onClose}>
            <div className="max-h-[80dvh] w-full max-w-lg space-y-2 overflow-y-auto rounded-t-box bg-base-200 p-4 sm:rounded-box"
                onClick={(e) => e.stopPropagation()}>
                <h2 className="mb-2 text-lg font-bold">{title}</h2>
                {children}
                <button type="button" className="btn btn-ghost btn-block mt-2" onClick={onClose}>
                    Fermer
                </button>
            </div>
        </div>
    );
}
